import Joi from "joi";

export const ValidationSchema = {
  StoreDetails: Joi.object({
    id: Joi.number().required(),
    store_active: Joi.boolean().required().messages({
      "boolean.base": "Is Active must be a boolean value.",
      "any.required": "Is Active is required.",
    }),

    bpp_name: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "Store Title is required.",
        "string.min": "Store Title must be at least 3 characters long.",
        "string.max": "Store Title cannot exceed 100 characters.",
        "string.pattern.base":
          "Store Title can only contain letters, numbers, spaces, hyphens, and apostrophes.",
      }),

    store_opening_time: Joi.string().required().messages({
      "string.empty": "Opening time is required.",
    }),

    store_closing_time: Joi.string().required().messages({
      "string.empty": "Closing time is required.",
    }),

    store_active: Joi.boolean().required().messages({
      "boolean.base": "Store active status is required.",
    }),

    bpp_symbol: Joi.alternatives()
      .try(
        Joi.string()
          .custom((value, helpers) => {
            try {
              const parsedValue = JSON.parse(value);
              if (
                typeof parsedValue === "string" &&
                /^https?:\/\//.test(parsedValue)
              ) {
                return parsedValue;
              }
              return helpers.error("any.invalid");
            } catch {
              if (/^https?:\/\//.test(value)) {
                return value;
              }
              return helpers.error("any.invalid");
            }
          })
          .messages({
            "any.invalid": "Store logo must be a valid URL.",
          }),

        Joi.object()
          .instance(File)
          .custom((file, helpers) => {
            const allowedTypes = [
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/webp",
            ];
            const maxSize = 2 * 1024 * 1024;

            if (!allowedTypes.includes(file.type)) {
              return helpers.error("file.invalidType");
            }

            if (file.size > maxSize) {
              return helpers.error("file.maxSize");
            }

            return file;
          })
          .messages({
            "object.base": "Uploaded logo must be a valid file.",
            "file.invalidType":
              "Only JPEG, PNG, JPG, and WEBP files are allowed.",
            "file.maxSize": "File size must be less than 2MB.",
          })
      )
      .required()
      .messages({
        "any.required": "Store logo is required.",
      }),

    storeImages: Joi.array()
      .items(
        Joi.object().instance(File).messages({
          "object.instance": "Each store image must be a valid file.",
        })
      )
      .min(1)
      .messages({
        "array.base": "Store images must be an array.",
        "array.min": "At least one image is required.",
      }),

    bpp_short_desc: Joi.string().trim().required().min(10).max(255).messages({
      "string.empty": "Short description is required.",
      "string.min": "Short description must be at least 10 characters long.",
      "string.max": "Short description cannot exceed 255 characters.",
    }),

    bpp_long_desc: Joi.string().trim().required().min(20).max(1000).messages({
      "string.empty": "Long description is required.",
      "string.min": "Long description must be at least 20 characters long.",
      "string.max": "Long description cannot exceed 1000 characters.",
    }),
    store_address: Joi.object({
      locality: Joi.string().required().optional().max(100).messages({
        "string.max": "Locality cannot exceed 100 characters.",
        "string.empty": "Locality is required",
      }),
      street: Joi.string().required().optional().max(255).messages({
        "string.max": "Street address cannot exceed 255 characters.",
        "string.empty": "Street address is required",
      }),
      city: Joi.string().required().messages({
        "string.empty": "City is required.",
        "string.empty": "City is required",
      }),
      state: Joi.string().required().messages({
        "string.empty": "State is required.",
        "string.empty": "State is required",
      }),
      area_code: Joi.string()
        .required()
        .optional()
        .pattern(/^[0-9]{6}$/)
        .messages({
          "string.pattern.base": "Area code must be a valid 6-digit number.",
          "string.empty": "Area code is required",
        }),
      latitude: Joi.number().required().messages({
        "number.base": "Latitude must be a number.",
        "number.unsafe": "Latitude must be a safe number",
        "any.required": "Latitude is required.",
      }),

      longitude: Joi.number().required().messages({
        "number.base": "Longitude must be a number.",
        "number.unsafe": "Longitude must be a safe number",
        "any.required": "Longitude is required.",
      }),
    })
      .required()
      .messages({
        "string.empty": "Store address is required",
        "any.required": "Store address is required.",
      }),

    store_email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.email": "Store email must be a valid email address.",
        "string.empty": "Store email is required.",
      }),

    store_phone_number: Joi.string()
      .pattern(/^[0-9]{10}$/)
      .required()
      .messages({
        "string.pattern.base":
          "Store phone number must be a valid 10-digit number.",
        "string.empty": "Store phone number is required.",
      }),
  }),

  StoreSettings: Joi.object({
    id: Joi.number().required(),
    buyer_finder_fee_min: Joi.number().min(0).max(100).required().messages({
      "any.required": "Buyer Finder Fee (Min) is required.",
      "number.base": "Buyer Finder Fee (Min) must be a number.",
    }),
    buyer_finder_fee_max: Joi.number().min(0).max(100).required().messages({
      "any.required": "Buyer Finder Fee (Min) is required.",
      "number.base": "Buyer Finder Fee (Min) must be a number.",
    }),
    return_window: Joi.number()
      .integer()
      .min(0)
      .required()
      .when("is_products_returnable", {
        is: true,
        then: Joi.number().greater(0).required().messages({
          "number.greater":
            "Return window must be greater than 0 when products are returnable",
        }),
      })
      .messages({
        "number.base": "Return window must be a number",
        "number.integer": "Return window must be an integer",
        "number.min": "Return window cannot be negative",
        "any.required": "Return window is required",
      }),
    delivery_charges: Joi.number().integer().min(0).required().messages({
      "number.base": "Delivery Charges must be a number",
      "number.integer": "Delivery Charges must be an integer",
      "number.min": "Delivery Charges cannot be less than 0",
      "any.required": "Delivery Charges is required",
    }),

    delivery_tat: Joi.number().integer().greater(0).required().messages({
      "number.base": "Delivery TAT must be a number",
      "number.integer": "Delivery TAT must be an integer",
      "number.greater": "Delivery TAT must be greater than 0",
      "any.required": "Delivery TAT is required",
    }),

    is_products_returnable: Joi.boolean(),
    is_seller_pickup_return: Joi.boolean(),
    is_products_cancellable: Joi.boolean(),
    is_tracking_available: Joi.boolean(),
    is_all_india_serviceable: Joi.boolean(),
  }),

  BusinessDetails: Joi.object({
    id: Joi.number().required(),

    store_pan_number: Joi.string()
      .length(10)
      .pattern(/^[A-Z]{5}[0-9]{4}[A-Z]$/)
      .required()
      .messages({
        "string.empty": "PAN number is required.",
        "string.length": "PAN number must be exactly 10 characters.",
        "string.pattern.base":
          "PAN number must follow the format: 5 letters, 4 digits, and 1 letter (e.g., ABCDE1234F).",
      }),

    name_as_per_pan: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "Name as per PAN is required.",
        "string.min": "Name as per PAN must be at least 3 characters long.",
        "string.max": "Name as per PAN cannot exceed 100 characters.",
        "string.pattern.base":
          "Name as per PAN can only contain letters, numbers, spaces, hyphens, and apostrophes.",
      }),

    store_gst_number: Joi.string()
      .length(15)
      .pattern(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[A-Z0-9]{3}$/)
      .required()
      .messages({
        "string.empty": "GST number is required.",
        "string.length": "GST number must be exactly 15 characters.",
        "string.pattern.base":
          "GST number must follow the format: 2 digits, 5 letters, 4 digits, 1 letter, and 3 alphanumeric characters (e.g., 22ABCDE1234F1Z5).",
      }),

    authorised_signatory: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "Authorised Signatory name  is required.",
        "string.min":
          "Authorised Signatory name must be at least 3 characters long.",
        "string.max":
          "Authorised Signatory name  cannot exceed 100 characters.",
        "string.pattern.base":
          "Authorised Signatory name  can only contain letters, numbers, spaces, hyphens, and apostrophes.",
      }),
    date_of_incorporation: Joi.date().required().max("now").messages({
      "any.required": "Date of incorporation is required.",
      "date.base": "Date of incorporation must be a valid date.",
      "date.max": "Date of incorporation cannot be a future date.",
    }),
  }),

  BankDetails: Joi.object({
    id: Joi.number().required(),

    beneficiary_name: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "Beneficiary name   is required.",
        "string.min": "Beneficiary name  must be at least 3 characters long.",
        "string.max": "Beneficiary name   cannot exceed 100 characters.",
        "string.pattern.base":
          "Beneficiary name   can only contain letters, numbers, spaces, hyphens, and apostrophes.",
      }),

    settlement_bank_account_no: Joi.string()
      .pattern(/^\d{9,18}$/)
      .required()
      .messages({
        "string.empty": "Bank account number is required.",
        "string.pattern.base":
          "Bank account number must be between 9 to 18 digits.",
        "any.required": "Bank account number is required.",
      }),

    settlement_ifsc_code: Joi.string()
      .pattern(/^[A-Z]{4}0[A-Z0-9]{6}$/)
      .required()
      .messages({
        "string.empty": "IFSC code is required.",
        "string.pattern.base":
          "IFSC code must follow the standard format (e.g., ABCD0123456).",
        "any.required": "IFSC code is required.",
      }),

    bank_name: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "Bank Name is required.",
        "string.min": "Bank Name must be at least 3 characters long.",
        "string.max": "Bank Name cannot exceed 100 characters.",
        "string.pattern.base":
          "Bank Name must only contain alphabets and spaces.",
      }),

    branch_name: Joi.string()
      .trim()
      .pattern(/^[a-zA-Z0-9\s.,'-]+$/)
      .required()
      .messages({
        "string.empty": "Branch name is required.",
        "string.pattern.base": "Branch name contains invalid characters.",
        "any.required": "Branch name is required.",
      }),
  }),

  CustomerSupport: Joi.object({
    id: Joi.number().required(),

    support_email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.empty": "Support email is required.",
        "string.email": "Support email must be a valid email address.",
        "any.required": "Support email is required.",
      }),

    support_phone_number: Joi.string()
      .pattern(/^[0-9]{10}$/)
      .required()
      .messages({
        "string.empty": "Support phone number is required.",
        "string.pattern.base":
          "Support phone number must be a 10-digit number.",
        "any.required": "Support phone number is required.",
      }),

    gro_name: Joi.string()
      .trim()
      .min(3)
      .max(100)
      .regex(/^[a-zA-Z0-9\s\-']+$/)
      .required()
      .messages({
        "string.empty": "GRO Name is required.",
        "string.min": "GRO Name must be at least 3 characters long.",
        "string.max": "GRO Name cannot exceed 100 characters.",
        "string.pattern.base":
          "GRO Name must only contain alphabets and spaces.",
      }),

    gro_phone: Joi.string()
      .pattern(/^[0-9]{10}$/)
      .required()
      .messages({
        "string.empty": "GRO phone is required.",
        "string.pattern.base": "GRO phone must be a 10-digit number.",
        "any.required": "GRO phone number is required.",
      }),

    gro_email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        "string.empty": "GRO email is required.",
        "string.email": "GRO email must be a valid email address.",
        "any.required": "GRO email is required.",
      }),

    gro_address: Joi.object({
      locality: Joi.string().required().messages({
        "string.empty": "Locality is required.",
        "any.required": "Locality is required.",
      }),

      street: Joi.string().required().messages({
        "string.empty": "Street is required.",
        "any.required": "Street is required.",
      }),

      city: Joi.string().required().messages({
        "string.empty": "City is required.",
        "any.required": "City is required.",
      }),

      state: Joi.string().required().messages({
        "string.empty": "State is required.",
        "any.required": "State is required.",
      }),

      area_code: Joi.string()
        .pattern(/^[0-9]{6}$/)
        .required()
        .messages({
          "string.empty": "Pincode is required.",
          "string.pattern.base": "Pincode must be a 6-digit number.",
          "any.required": "Pincode is required.",
        }),

      latitude: Joi.number().required().messages({
        "number.base": "Latitude must be a number.",
        "number.unsafe": "Latitude must be a safe number",
        "any.required": "Latitude is required.",
      }),

      longitude: Joi.number().required().messages({
        "number.base": "Longitude must be a number.",
        "number.unsafe": "Longitude must be a safe number",
        "any.required": "Longitude is required.",
      }),
    })
      .required()
      .messages({
        "any.required": "Address is required.",
      }),

    gro_profile_image: Joi.alternatives()
      .try(
        Joi.string()
          .custom((value, helpers) => {
            try {
              const parsedValue = JSON.parse(value);
              if (
                typeof parsedValue === "string" &&
                /^https?:\/\//.test(parsedValue)
              ) {
                return parsedValue;
              }
              return helpers.error("any.invalid");
            } catch {
              if (/^https?:\/\//.test(value)) {
                return value;
              }
              return helpers.error("any.invalid");
            }
          })
          .messages({
            "any.invalid": "GRO Profile must be a valid URL.",
          }),
        Joi.object().instance(File).messages({
          "object.base": "Uploaded GRO Profile must be a valid file.",
        })
      )
      .required()
      .messages({
        "any.required": "GRO Profile is required.",
      }),
  }),
  ServiceabilityAndDeliveryCharges: Joi.object({}),
};
